import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import CustomLink from '../../../../shared/components/custom-link/CustomLink';
import { createCustomColumn, modelTemplate } from '../../../../shared/helpers/ColumnHelper';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';
import { MetadataBadge } from '../../../../shared/components/metadata-badge/MetadataBadge';
import { getVerifiedBrandStatus, isFirmMentionedInBrands } from '../../../sources/helpers/SourcesHelper';
import { TagsResponse } from '../../../../shared/models/tags/TagsResponse';
import { LinksData } from '../../../prompt/preview/components/stats/models/StatsModel';
import { ChatType, getChatTypeToName, projectChatType } from '../../../../shared/types/monitor/ChatType';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import {
  useEffectiveAddons,
  useEffectiveFeatures,
} from '../../../../shared/contexts/effective-features/EffectiveFeaturesContext';
import { AccountResponse } from '../../../../shared/models/account/AccountResponse';
import CitationsBox from './CitationsBox';
import {
  expandSelectedChatTypes,
  getMonitorTypesForChatType,
  projectChatTypesWithData,
} from '../../../../shared/helpers/MonitorTypeHelpers';
import { calculatePercentageShare, getAllSourcesCount } from '../../../../shared/helpers/StatsHelpers';
import { Dropdown } from 'primereact/dropdown';
import { DropdownItemData } from '../../../../shared/components/dropdown-item/DropdownItem';
import { useFilters } from '../../../../shared/contexts/filters/FiltersContext';
import {
  filterSourcesByDomain,
  findSelectedDomain,
  getDomainsOptions,
} from '../../../prompt/preview/components/stats/helpers/StatsHelper';
import { Button } from 'primereact/button';
import useExport from '../../../../shared/hooks/global/UseExport';
import CustomBarChart from '../../../../shared/components/chart/custom-bar-chart/CustomBarChart';
import {
  getVerticalBarChartSize,
  prepareAndGetHorizontalBarOptions,
} from '../../../../shared/components/chart/custom-bar-chart/helpers/CustomBarChartHelper';
import { useLegendState } from '../../../../shared/components/chart/custom-bar-chart/hooks/UseLegendState';
import { renderToStaticMarkup } from 'react-dom/server';
import ChartTooltip from '../../../../shared/components/chart/custom-bar-chart/components/chart-tooltip/ChartTooltip';
import { useChartColorPalette } from '../../../../shared/components/chart/custom-bar-chart/hooks/UseChartColorPalette';
import './LinkTrackingTable.scss';
import usePermission from '../../../../shared/hooks/global/UsePermission';
import { useTheme } from '../../../../shared/contexts/theme/ThemeContext';

interface LinkTrackingTableProps {
  domains: string[];
  sources: LinksData[];
  isAllSelected: boolean;
  exportSources: () => void;
  isDemo: boolean;
  /**
   * Resolved brand label (e.g. `firmNameLabel`). When provided, a "mention" column is shown
   * indicating whether this brand was found on the verified source page. Omit to hide the column.
   */
  firmName?: string;
  tags?: TagsResponse[];
}

const LinkTrackingTable: React.FC<LinkTrackingTableProps> = ({
  domains,
  sources,
  isAllSelected,
  exportSources,
  isDemo,
  firmName,
  tags = [],
}) => {
  const isMobile = useIsMobile();
  const projectsAccess = usePermission('PROJECTS');
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { accountResponse } = useAccount();
  // Owner's models/addons when a superadmin views a client's project; own config elsewhere.
  const effectiveFeatures = useEffectiveFeatures();
  const effectiveAddons = useEffectiveAddons();
  const { domain, setDomain, selectedChatTypes } = useFilters();
  const { canExport } = useExport();
  const [searchParams] = useSearchParams();

  const [isDownloading, setIsDownloading] = useState(false);

  const hasExplicitModelFilter = searchParams.get('models') !== null;

  const allowedMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter) return null;
    if (!selectedChatTypes || selectedChatTypes.length === 0) return new Set<string>();
    const expanded = expandSelectedChatTypes(selectedChatTypes);
    const types = new Set<string>();
    for (const ct of Array.from(expanded)) {
      for (const mt of getMonitorTypesForChatType(ct as ChatType)) {
        types.add(mt);
      }
    }
    return types;
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const modelFilteredSources = useMemo(() => {
    if (!allowedMonitorTypes) return sources;
    return sources.filter((s) => s.models.some((m) => allowedMonitorTypes.has(m)));
  }, [sources, allowedMonitorTypes]);

  const filteredSources = useMemo(
    () => filterSourcesByDomain(domain, modelFilteredSources),
    [modelFilteredSources, domain]
  );

  const domainsOptions: DropdownItemData<string>[] = useMemo(
    () => getDomainsOptions(domains, domain),
    [domains, domain, t]
  );

  const selectedOption = useMemo(
    () => findSelectedDomain(domain, domainsOptions),
    [domain, domainsOptions]
  );

  const handleDomainChange = (name: string) => {
    if (name === t('allDomains')) {
      setDomain(null);
    } else {
      setDomain(name);
    }
  };

  const handleExportSources = async () => {
    if (isDownloading || !canExport) {
      return;
    }

    setIsDownloading(true);

    try {
      await exportSources();
    } finally {
      setTimeout(() => {
        setIsDownloading(false);
      }, 500);
    }
  };

  const allSourcesCount = useMemo(() => getAllSourcesCount(modelFilteredSources), [modelFilteredSources]);

  const visibleChatTypes = useMemo(() => {
    // NEW_STATS accounts: every model we have sources for; the account limit lives on the filter
    // (allowedMonitorTypes) only. Without the addon, keep the legacy account-feature list.
    const hasNewStats = effectiveAddons.includes('NEW_STATS');
    let all: ChatType[];
    if (hasNewStats) {
      const present = new Set<string>();
      sources.forEach((s) => s.models.forEach((m) => present.add(m)));
      all = projectChatTypesWithData(present);
    } else {
      const effectiveAccount: AccountResponse | null = accountResponse
        ? { ...accountResponse, features: effectiveFeatures as AccountResponse['features'] }
        : null;
      all = projectChatType(effectiveAccount, true, true) as ChatType[];
    }
    if (!allowedMonitorTypes) return all;
    return all.filter((ct) =>
      getMonitorTypesForChatType(ct as ChatType).some((mt) => allowedMonitorTypes.has(mt))
    );
  }, [sources, allowedMonitorTypes, accountResponse, effectiveFeatures, effectiveAddons]);

  const sourcesDataString: string[][] = useMemo(
    () => [
      ['Label', 'Value'],
      ...visibleChatTypes.map((chatType) => {
        const sourcesCount = filteredSources
          .filter((linkData) =>
            linkData.models.some((model) =>
              getMonitorTypesForChatType(chatType as ChatType).includes(model)
            )
          )
          .map((linkData) => linkData.count / linkData.models.length);

        const total = sourcesCount.reduce((sum, value) => sum + value, 0);
        return [
          getChatTypeToName(chatType as ChatType),
          calculatePercentageShare(total, allSourcesCount, false),
        ];
      }),
    ],
    [visibleChatTypes, filteredSources, allSourcesCount]
  );

  const legendData = useChartColorPalette(sourcesDataString, true);

  const colorMap = useMemo(
    () => new Map(legendData.map((item) => [item.label, item.color])),
    [legendData]
  );

  const tooltipFormatter = (params: any): string =>
    renderToStaticMarkup(<ChartTooltip tooltipFormatterParams={params} />);

  const initialLabels = useMemo(
    () => sourcesDataString.slice(1).map((item) => item[0]),
    [sourcesDataString]
  );

  const { legendStateMap, handleLegendClick } = useLegendState(initialLabels);

  const filteredChartData = useMemo(
    () => sourcesDataString.filter((row, index) => index === 0 || legendStateMap.get(row[0]) !== false),
    [sourcesDataString, legendStateMap]
  );

  // `theme` w zależnościach: kolory dymka i osi (`getChartThemeColors`) są czytane przy budowaniu opcji,
  // więc bez niego po przełączeniu na ciemny motyw dymek zostawał jasny.
  const chartOptions = useMemo(
    () => prepareAndGetHorizontalBarOptions(tooltipFormatter, filteredChartData, colorMap),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `theme` celowo: kolory czytane w środku
    [filteredChartData, colorMap, theme]
  );

  if (!sources || sources.length === 0 || !domains || domains.length === 0) {
    return null;
  }

  return (
    <div className="page-box__stats">
      <div className={isAllSelected ? `stats` : ''}>
        <div className="stats__panel">
          <div id="key-sources">
            <div className="page-box__title-action">
              <div className="page-box__title page-box__title--flex">
                {t('linkTracking')}
                <CustomTooltip tooltipText="linkTrackingTooltip" />
              </div>
              <div className="page-box__source-filter">
                <Dropdown
                  value={selectedOption}
                  placeholder={t('selectDomain')}
                  onChange={(e) => handleDomainChange(e.value.name)}
                  options={domainsOptions}
                  className="dropdown"
                  optionLabel="name"
                  optionValue="type"
                  filter
                />
                {!isDemo && projectsAccess.write && (
                  <>
                    <Button
                      id="btn-settings-export-sources"
                      className={`action-btn  ${!canExport ? 'btn--disabled-tooltip' : ''}`}
                      onClick={() => handleExportSources()}
                    >
                      {isDownloading ? (
                        <span className="ico--loader ico--xs" />
                      ) : (
                        <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
                      )}
                    </Button>
                    <CustomTooltip
                      tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadSources'}
                      targetRef="btn-settings-export-sources"
                    />
                  </>
                )}
              </div>
            </div>
            <DataTable
              stripedRows
              paginator={true}
              responsiveLayout="stack"
              paginatorClassName="pagination"
              removableSort
              rows={20}
              rowsPerPageOptions={[10, 20, 50]}
              value={filteredSources}
              tableStyle={{ minWidth: '40rem', tableLayout: 'auto' }}
              tableClassName="table"
              className="table-wrapper table-wrapper--stack"
              currentPageReportTemplate={t('rowsNumber') + ':'}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            >
              <Column
                field="index"
                header={t('position')}
                headerClassName="table__header table__header--center"
                bodyClassName="table__cell table__cell--center table__cell--stack-rank"
                style={{ width: '50px' }}
                body={(_rowData: LinksData, { rowIndex }: { rowIndex: number }) => {
                  return `#${rowIndex + 1}`;
                }}
              />
              <Column
                field="url"
                bodyClassName="table__cell--stack-title"
                header={t('url')}
                headerClassName="table__header"
                style={{ maxWidth: '220px' }}
                body={(rowData) => (
                  <div className="source-data">
                    <span
                      className="source-data__icon"
                      style={{
                        backgroundImage: `url("https://favicon.is/${rowData.url}"`,
                      }}
                      // backgroundImage: `
                      // ${
                      //   accountResponse?.userId === 1
                      //     ? 'url("https://logohub.eu/img/scrape/' +
                      //       rowData.url +
                      //       '?token=pk_73050b76958546a3b9e6489bde81e1c4")'
                      //     : 'url("https://favicon.is/' + rowData.url + '")'
                      // }`,
                      // }}
                    />
                    <div className="source-data__details source-data__details--basic">
                      {rowData.title && (
                        <span className="source-data__title text-ellipsis">{rowData.title}</span>
                      )}
                      <span className="text-ellipsis">
                        <CustomLink url={rowData.url} target="_blank" />
                      </span>
                    </div>
                  </div>
                )}
              />
              <Column
                field="model"
                header={t('model')}
                headerClassName="table__header"
                style={{ width: '200px' }}
                body={modelTemplate}
              />
              {firmName && (
                <Column
                  field="isVerified"
                  header={t('mentions')}
                  headerClassName="table__header table__header--center"
                  bodyClassName="table__cell table__cell--center"
                  style={{ width: '120px' }}
                  body={(rowData: LinksData, { rowIndex }: { rowIndex: number }) => {
                    const { variant, statusText, tooltipText } = getVerifiedBrandStatus(
                      rowData.isVerified,
                      isFirmMentionedInBrands(rowData.brands, firmName, tags),
                      firmName,
                      t
                    );
                    return (
                      <MetadataBadge
                        variant={variant}
                        text={statusText}
                        tooltipText={tooltipText}
                        uniqueId={`link-tracking-brand-tooltip-${rowIndex}`}
                      />
                    );
                  }}
                />
              )}
              {createCustomColumn({
                plainHeader: isMobile,
                field: 'count',
                body: (data: LinksData) => calculatePercentageShare(data.count, allSourcesCount),
                headerName: 'coverage',
                tooltip: t('coverageTooltip'),
                headerClassName: 'table__header table__header--center',
                bodyClassName: 'table__cell table__cell--center',
                style: { width: '50px' },
              })}
            </DataTable>
          </div>
        </div>
        {isAllSelected && (
          <div className="page-box__column">
            <div className="stats__panel">
              <div className="page-box__title-action">
                <div className="page-box__title page-box__title--flex">{t('aiCitations')}</div>
              </div>
              <div className="stats-box stats-box--citations">
                {visibleChatTypes.map((chatType: string, index) => (
                  <CitationsBox
                    chatType={chatType as ChatType}
                    linksData={filteredSources}
                    key={`citations-box-${index}`}
                  />
                ))}
              </div>
            </div>
            {sourcesDataString.length > 0 && (
              <div className="stats__panel">
                <div className="stats-box stats-box--pie-chart stats-box--pie-chart-sm">
                  <div className="stats-box__content">
                    <CustomBarChart
                      customEChartsOption={{ eChartsOption: chartOptions, legendData }}
                      legendStateMap={legendStateMap}
                      onLegendClick={handleLegendClick}
                      chartStyle={getVerticalBarChartSize}
                      type={'HORIZONTAL'}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkTrackingTable;
